import { ConnectedPlumaProvider } from '@/components/connected-pluma-provider';
import React, { Suspense } from 'react';
import { ActiveAppContextProvider } from '@/context/active-bundle';
import { BackgroundAppBundleContextProvider } from '@/context/background-app-bundle';
import { ThemeContextProvider } from '@/context/theme';
import { getSampleAccounts } from '@/features/workspaces';
import { useInjectImportShim } from '@/hooks/use-inject-import-shim';
import { useLoadShims } from '@/hooks/use-load-shims';
import { RouterProvider } from '@/services/router';
import { recordRumMilestone, RumMilestone } from '@/services/rum';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { Outlet, useLocation, useRouter } from '@tanstack/react-router';
import { activeRouteStore, childAppLoadedStore, setActiveRoute } from '@customerio/hydra-nav-state';
import { useHydraNavigationHandoff } from './use-hydra-navigation-handoff';
import { usePreloadWorkspaceData } from './use-preload-workspace-data';
import { useTrackingScriptsInjection } from './use-tracking-scripts-injection';
import { usePendingSignupCompletion } from '@/services/analytics/use-pending-signup-completion';
import { Box, PageLoader } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { LazyMotion } from 'motion/react';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect } from 'react';
import { NavigationGuardProvider } from '@/services/navigation-guard';
import { CurrentWorkspaceProvider } from '@/context/current-workspace';
import { DerivedPermissionsProvider } from '@/services/permissions';
import { preloadJourneysAppBundle } from '@/pages/journeys';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import * as styles from './index.css';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { useRouteOwnerBodyAttribute } from './use-route-owner-body-attribute';
import { getNextActiveRoute, getRouterLocationPath, shouldSyncActiveRouteToRouter } from './active-route-sync';
import { syncRouterLocationWithoutLoad } from '@/services/router/embedded-app-router-bridge';
import { queryClient } from '@/services/query-client';

const DevHelper = React.lazy(() => import('./components/dev-helper').then((m) => ({ default: m.DevHelper })));

const prefetchSampleAccounts = async () => {
  await queryClient.prefetchQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });
};

export default function Root() {
  const loaded = useLoadShims();

  useMountEffect(() => {
    prefetchSampleAccounts();
  });

  useEffect(() => {
    if (loaded) {
      preloadJourneysAppBundle();
    }
  }, [loaded]);

  performance.mark('hydra:root:render');
  recordRumMilestone(RumMilestone.ShellRendered);

  return (
    <HelmetProvider>
      <LazyMotion features={() => import('./utils/motion-features').then((mod) => mod.domMax)}>
        <QueryClientProvider client={queryClient}>
          <ActiveAppContextProvider>
            <BackgroundAppBundleContextProvider>
              <RouterProvider>
                <ThemeContextProvider>
                  {/* Global/service context lives above Pluma so it's available inside Pluma
                      managed modals (which render at the root Pluma provider). CurrentWorkspace
                      and permissions resolve from stores + react-query + router, all above Pluma. */}
                  <CurrentWorkspaceProvider>
                    <DerivedPermissionsProvider>
                      <ConnectedPlumaProvider>
                        <NuqsAdapter>
                          <NavigationGuardProvider>{loaded ? <App /> : null}</NavigationGuardProvider>
                        </NuqsAdapter>
                      </ConnectedPlumaProvider>
                    </DerivedPermissionsProvider>
                  </CurrentWorkspaceProvider>
                </ThemeContextProvider>
              </RouterProvider>
            </BackgroundAppBundleContextProvider>
          </ActiveAppContextProvider>
        </QueryClientProvider>
      </LazyMotion>
    </HelmetProvider>
  );
}

type AppContainerProps = { children?: ReactNode };

function AppContainer({ children }: AppContainerProps) {
  return (
    <div className={styles.appRoot}>
      <Box id="in-app-target_root-announcement" flexShrink="0" />
      {children}

      <Suspense>
        <DevHelper />
      </Suspense>
    </div>
  );
}

function App() {
  const location = useLocation();
  const router = useRouter();

  const safeNavigate = useSafeNavigate();

  const isLoaded = useInjectImportShim();
  const activeRoute = useStore(activeRouteStore);
  const childAppLoaded = useStore(childAppLoadedStore);

  useTrackingScriptsInjection();
  usePreloadWorkspaceData();
  // A fresh signup's `Signup: Completed` waits here for its workspace, not on any one page.
  usePendingSignupCompletion();

  // Set activeRoute for Hydra-only URLs when location changes
  // This handles cases where no child app is loaded to call setActiveRoute
  useEffect(() => {
    const currentPath = getRouterLocationPath(location);
    const nextActiveRoute = getNextActiveRoute(activeRoute, currentPath);
    if (nextActiveRoute != null) {
      setActiveRoute(nextActiveRoute);
    }
  }, [location.pathname, location.searchStr, location.hash, activeRoute]);

  // uses useLayoutEffect, rather than useEffect, to ensure this runs before
  // other effects have a chance to run, or components have a chance to render.
  useLayoutEffect(() => {
    const currentPath = getRouterLocationPath(location);
    if (!shouldSyncActiveRouteToRouter(activeRoute, currentPath, childAppLoaded)) {
      return;
    }

    // Embedded apps already own the browser history transition. Keep TanStack
    // Router's location state in sync without loading a new route branch.
    syncRouterLocationWithoutLoad(router);
  }, [activeRoute, childAppLoaded, location.pathname, location.searchStr, location.hash, router]);

  // Ember→Hydra navigation handoff + the one-time persisted-snackbar replay that
  // rides along with it (covers both in-app and full-document handoffs).
  useHydraNavigationHandoff(safeNavigate);

  useRouteOwnerBodyAttribute();

  return <AppContainer>{isLoaded ? <Outlet /> : <PageLoader />}</AppContainer>;
}
